import React, { useId, useState } from 'react';
import { setBundleFloorBinQuantity } from '../../services/bundleFloorAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import { parseWholeNumberInput } from '../orders/orderdetail/bundleWorkOrderLogic';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import ProductImage from './ProductImage';
import type { BundleFloorBinLevel, BundleFloorLevel } from '../../schemas/apiResponse.schemas';

interface BundleBinQuantityModalProps {
  readonly sku: string;
  readonly bin: BundleFloorBinLevel;
  readonly onClose: () => void;
  readonly onUpdated: (floor: BundleFloorLevel, quantity: number) => void;
}

/**
 * Absolute quantity for one bundle pick bin (same `set_quantity` action as the admin thresholds
 * per-bin edit). Kept separate from `BundleWorkBaselineModal` because that modal's styles live in
 * the pick-list stylesheet, which the label modal's pages do not load.
 */
function BundleBinQuantityModal({ sku, bin, onClose, onUpdated }: BundleBinQuantityModalProps): React.ReactElement {
  const inputId = useId();
  const [value, setValue] = useState(String(bin.quantity));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (): void => {
    if (!submitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const parsed = parseWholeNumberInput(value);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const updated = await setBundleFloorBinQuantity(sku, bin.storageIdPrefix, parsed.qty);
      onUpdated(updated, parsed.qty);
    } catch (err: unknown) {
      console.error('Error updating bundle bin quantity:', err);
      setError(getApiErrorMessage(err, 'Failed to update bin quantity. Please try again.'));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="pbl-modal-overlay sw-bin-qty-overlay"
      onClick={(event) => {
        event.stopPropagation();
        handleClose();
      }}
      role="presentation"
    >
      <div
        className="pbl-modal sw-bin-qty-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby={`${inputId}-title`}
        aria-modal="true"
      >
        <button type="button" className="pbl-modal-close" onClick={handleClose} aria-label="Close">
          ×
        </button>
        <div className="sw-bin-qty-header">
          <ProductImage sku={sku} alt={sku} />
          <div className="sw-bin-qty-sku">{renderSkuWithSoftBreaks(sku)}</div>
        </div>
        <h3 id={`${inputId}-title`} className="sw-bin-qty-title">Update bin quantity</h3>
        <div className="sw-bin-qty-chips">
          <div className="sw-bin-qty-chip">
            <span className="sw-bin-qty-chip-label">Shelf</span>
            <span className="sw-bin-qty-chip-value">{bin.shelfIndex ?? 'Unindexed'}</span>
          </div>
          <div className="sw-bin-qty-chip">
            <span className="sw-bin-qty-chip-label">In bin now</span>
            <span className="sw-bin-qty-chip-value">{bin.quantity}</span>
          </div>
        </div>
        <p className="sw-bin-qty-meta">ID {bin.storageIdPrefix}</p>
        <form className="sw-bin-qty-form" onSubmit={(event) => { void handleSubmit(event); }}>
          <label className="sw-bin-qty-label" htmlFor={inputId}>Quantity in bin</label>
          <input
            id={inputId}
            className="sw-bin-qty-input"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            disabled={submitting}
            onChange={(event) => setValue(event.target.value)}
            onFocus={(event) => event.target.select()}
            autoFocus
          />
          {error ? <p className="sw-bin-qty-error" role="alert">{error}</p> : null}
          <div className="sw-bin-qty-actions">
            <button
              type="button"
              className="sw-label-checkout-btn sw-bin-qty-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="sw-label-checkout-btn sw-label-bin-qty-btn"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BundleBinQuantityModal;
